import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Ruler, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Reviews } from "@/components/Reviews";
import { FAQ } from "@/components/FAQ";
import { Seo } from "@/components/Seo";
import { IMG, PROJECTS } from "@/data/projects";

const words = ["Kozijnen die", "van uw huis", "een thuis maken."];

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="DEKO Kozijnen | Kunststof kozijnen, deuren & schuifpuien in Noord-Nederland"
        description="DEKO Kozijnen levert en plaatst hoogwaardige kunststof kozijnen, deuren en schuifpuien in Noord-Nederland. 15 jaar garantie, eigen monteurs. Vraag vrijblijvend een offerte aan."
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pb-16 pt-32 overflow-hidden">
        <motion.img
          src={IMG.heroExterior}
          alt="Moderne woning met antraciet kozijnen"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
        <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 md:px-10 text-white">
          <h1 className="font-display font-extrabold leading-[0.98] text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            {words.map((w, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }} className="block">
                  {w}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }} className="mt-8 max-w-xl text-base sm:text-lg text-white/80 font-light leading-relaxed">
            Wij leveren en plaatsen hoogwaardige kunststof kozijnen, deuren en schuifpuien. Vakmanschap, kwaliteit en een perfecte afwerking staan bij ons centraal.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/offerte" data-testid="hero-primary-cta" className="group inline-flex items-center justify-center gap-3 bg-white text-[#2F3438] px-7 py-4 rounded-sm hover:bg-white/90 transition-colors duration-300">
              <span className="text-sm font-medium">Vraag vrijblijvend een offerte aan</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link to="/projecten" data-testid="hero-secondary-cta" className="inline-flex items-center justify-center gap-3 border border-white/40 text-white px-7 py-4 rounded-sm hover:bg-white/10 transition-colors duration-300 text-sm font-medium">
              Bekijk onze projecten
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SLOGAN / TRUST */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">Kwaliteit waar je op kunt bouwen</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6 text-[#2F3438] max-w-2xl">
              Vakmanschap dat je ziet, isolatie die je voelt.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-[#222]/60 text-lg font-light leading-relaxed">
              DEKO Kozijnen staat voor doordacht vakwerk. Wij meten nauwkeurig in, produceren op maat en plaatsen met oog voor detail — zodat uw woning stiller, warmer en mooier wordt.
            </p>
          </Reveal>
        </div>
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#ECECEA] border border-[#ECECEA]">
          {[
            { icon: Ruler, t: "Op maat gemaakt", b: "Elk kozijn exact ingemeten en geproduceerd." },
            { icon: ShieldCheck, t: "15 jaar garantie", b: "Op materiaal én montage door eigen monteurs." },
            { icon: Sparkles, t: "Perfecte afwerking", b: "Strakke lijnen, geen kier, netjes opgeleverd." },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-white p-10">
              <s.icon className="h-7 w-7 text-[#2F3438]" strokeWidth={1.4} />
              <h3 className="font-display font-semibold text-xl mt-6 text-[#2F3438]">{s.t}</h3>
              <p className="text-[#222]/55 mt-2 font-light">{s.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SUBSIDIE CALLOUT */}
      <section className="bg-[#2F3438] text-white py-20 md:py-24" data-testid="home-subsidie-callout">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-8">
            <span className="text-xs uppercase tracking-[0.25em] text-white/40">Subsidie in Fryslân</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6 max-w-2xl">
              Tot € 111 per m² ISDE-subsidie op triple glas in nieuwe kozijnen.
            </h2>
            <p className="text-white/65 text-lg font-light leading-relaxed mt-5 max-w-xl">
              Naast de landelijke ISDE zijn er provinciale en gemeentelijke regelingen. DEKO zoekt vrijblijvend voor u
              uit welke subsidies in uw situatie mogelijk zijn — onderbouwd met officiële bronnen.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 flex flex-col gap-4">
            <Link to="/subsidie" data-testid="home-subsidiecheck-cta" className="group inline-flex items-center justify-between gap-3 bg-white text-[#2F3438] px-7 py-4 rounded-sm hover:bg-white/90 transition-colors duration-300">
              <span className="text-sm font-medium">Doe de vrijblijvende subsidiecheck</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link to="/subsidie" data-testid="home-subsidie-info-link" className="inline-flex items-center justify-between gap-3 border border-white/30 text-white px-7 py-4 rounded-sm hover:bg-white/10 transition-colors duration-300 text-sm font-medium">
              Alles over subsidies in Fryslân <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="bg-[#F7F7F5] py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal><h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2F3438] max-w-2xl">Ons vakgebied.</h2></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { to: "/kunststof-kozijnen", t: "Kunststof Kozijnen", img: IMG.interiorLake },
              { to: "/deuren", t: "Deuren", img: IMG.door1 },
              { to: "/schuifpuien", t: "Schuifpuien", img: IMG.sliding1 },
            ].map((c, i) => (
              <Reveal key={c.to} delay={i * 0.1}>
                <Link to={c.to} data-testid={`category-${c.to.slice(1)}`} className="group block">
                  <div className="relative overflow-hidden aspect-[4/5] rounded-sm bg-[#ECECEA]">
                    <img src={c.img} alt={c.t} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white flex items-center justify-center opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      <ArrowUpRight className="h-5 w-5 text-[#2F3438]" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5">
                    <h3 className="font-display font-semibold text-xl text-[#2F3438]">{c.t}</h3>
                    <span className="text-sm text-[#222]/40 group-hover:text-[#2F3438] transition-colors">Bekijk</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / INSTALLER */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
              <img src={IMG.installer} alt="Monteur meet nauwkeurig in" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs uppercase tracking-[0.25em] text-[#222]/40">Over DEKO</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6 text-[#2F3438]">Eigen vakmensen, geen concessies.</h2>
            <p className="text-[#222]/60 text-lg font-light leading-relaxed mt-6 max-w-lg">
              Van eerste opname tot oplevering werken wij met vaste, ervaren monteurs. Zo houden wij de kwaliteit volledig in eigen hand en weet u precies wie er bij u thuis komt.
            </p>
            <Link to="/over" data-testid="home-about-link" className="inline-flex items-center gap-3 mt-8 text-[#2F3438] group">
              <span className="text-sm font-medium border-b border-[#2F3438] pb-0.5">Lees meer over DEKO</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section className="bg-[#2F3438] text-white py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <Reveal><h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl">Recent gerealiseerd.</h2></Reveal>
            <Reveal delay={0.1}>
              <Link to="/projecten" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm">Alle projecten <ArrowUpRight className="h-4 w-4" /></Link>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PROJECTS.slice(0, 4).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div className="group">
                  <div className="relative overflow-hidden rounded-sm aspect-[3/4]">
                    <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-white/40">{p.cat}</div>
                    <div className="font-display font-medium mt-1">{p.place}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <Reviews />

      {/* FAQ */}
      <FAQ />

      {/* CTA BAND */}
      <section className="bg-white py-28 text-center">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl text-[#2F3438] leading-tight max-w-3xl mx-auto">Klaar om uw woning compleet te maken?</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/offerte" data-testid="cta-band-offerte" className="group inline-flex items-center gap-3 mt-10 bg-[#2F3438] text-white px-8 py-4 rounded-sm hover:bg-[#222] transition-colors duration-300">
              <span className="text-sm font-medium">Vraag vrijblijvend een offerte aan</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
